import React, { useEffect, useRef, useState } from 'react';
import { Modal } from './ui';

interface EditNameModalProps {
  isOpen: boolean;
  currentName: string | null;
  onClose: () => void;
  onSubmit: (newName: string) => Promise<void>;
}

const TITLE_ID = 'edit-name-title';
const MAX_NAME_LEN = 100;

export const EditNameModal: React.FC<EditNameModalProps> = ({
  isOpen,
  currentName,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName(currentName ?? '');
      setError(null);
      setSubmitting(false);
      requestAnimationFrame(() => inputRef.current?.select());
    }
  }, [isOpen, currentName]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Name can't be empty.");
      inputRef.current?.focus();
      return;
    }
    if (trimmed === currentName) {
      onClose();
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Could not update name.');
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isBusy={submitting} labelledBy={TITLE_ID}>
      <form onSubmit={handleSubmit} noValidate>
        <Modal.Header
          title="Change name"
          titleId={TITLE_ID}
          subtitle="How PaperBrain addresses you."
        />

        <Modal.Body>
          <label className="pb-modal-field pt-4">
            <span className="pb-modal-label">Name</span>
            <input
              ref={inputRef}
              type="text"
              className="pb-modal-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              autoComplete="name"
              maxLength={MAX_NAME_LEN}
              disabled={submitting}
            />
          </label>

          {error && <p className="pb-modal-error" role="alert">{error}</p>}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="pb-modal-btn pb-modal-btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="pb-modal-btn pb-modal-btn--primary"
            disabled={submitting || !name.trim()}
          >
            {submitting ? 'Saving…' : 'Save name'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
